import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '@/components/ui/form'
import { RotateCcw } from 'lucide-react'
import { PropsWithChildren, ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Card from '../Card'
import { cn } from '@/lib/utils'

interface Props {
  label: string | ReactNode
  description?: ReactNode
  layout?: 'box' | 'inline' | 'stacked' | 'inline-shrinkable'
  resetValue?: string
  className?: string
  keepRowOnMobile?: boolean
}

const FieldWrapper = ({
  children,
  className,
  layout = 'inline',
  label,
  description,
  resetValue,
  keepRowOnMobile = false,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation()
  const form = useFormContext()
  const field = useFormField()
  const currentValue = form.watch(field.name)

  if (layout === 'box') {
    return (
      <Card className='mb-6'>
        <FormItem className={cn('grow', className)}>
          <div
            className={cn(
              'flex gap-2',
              keepRowOnMobile
                ? 'flex-row items-center justify-between'
                : 'flex-col md:flex-row md:items-center md:justify-between',
            )}
          >
            <div className='min-w-0 space-y-1'>
              {label ? <FormLabel>{label}</FormLabel> : null}
              {description ? (
                <FormDescription>{description}</FormDescription>
              ) : null}
            </div>
            <FormControl className='shrink-0'>{children}</FormControl>
          </div>
          <FormMessage />
        </FormItem>
      </Card>
    )
  }

  const showReset =
    layout === 'inline' &&
    resetValue !== undefined &&
    currentValue !== resetValue

  return (
    <div
      className={
        layout === 'stacked' || layout === 'inline-shrinkable' ? '' : 'mb-6'
      }
    >
      <FormItem
        className={`${layout === 'inline-shrinkable' ? '' : 'grow'} ${
          className ?? ''
        }`}
      >
        {label ? (
          <FormLabel className='inline-flex w-full items-center justify-between'>
            {label}
            {showReset ? (
              <a
                onClick={() =>
                  form.setValue(field.name, resetValue, { shouldDirty: true })
                }
                className='cursor-pointer font-normal flex gap-1.5 items-center hover:text-white'
              >
                <RotateCcw size={14} /> {t('shared.reset')}
              </a>
            ) : null}
          </FormLabel>
        ) : null}
        <FormControl>{children}</FormControl>
        {layout !== 'stacked' || description ? (
          <FormDescription>{description}</FormDescription>
        ) : null}
        <FormMessage />
      </FormItem>
    </div>
  )
}

export default FieldWrapper
